import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from 'components/dialogs/ConfirmDialog';
import PromptDialog from 'components/dialogs/PromptDialog';
import useModal from 'hooks/useModal';
import { BLModel, BLTypeModel } from 'models/BLModel';
import BLTypeDialog from 'modules/voyages/dialogs/BLTypeDialog';
import BLShipperDialog from 'modules/voyages/dialogs/BLShipperDialog';
import blTypeList from 'recoil/atoms/BLTypeList';
import Api from 'utils/api';
import { removeItemAtIndex, replaceItemAtIndex } from 'utils/common';


const useBLModals = () => {
    const {t} = useTranslation();
    const setTypes = useSetRecoilState(blTypeList);
    const {showModal, hideModal} = useModal();

    const openAddBLTypeModal = () => showModal(BLTypeDialog,
        {},
        {
            title: t('add_title', {value: t('bl_type')})
        })
        .then(data => Api.bl.createType(data))
        .then(res => setTypes(old => [res.data, ...old]))
        .then(hideModal);

    const openEditBLTypeModal = (type: BLTypeModel) => showModal(BLTypeDialog,
        {
            type,
        },
        {
            title: t('edit_title', {value: t('bl_type')})
        })
        .then(data => Api.bl.updateType({...data, id: type.id}))
        .then(res => setTypes(old => replaceItemAtIndex(old, old.findIndex(i => i.id === type.id), res.data)))
        .then(hideModal);

    const confirmDeleteBLType = (type: BLTypeModel) => showModal(ConfirmDialog,
        {
            description: `${type.type} ${type.name}`
        },
        {
            title: t('confirm_delete_bl_type'),
            fullScreen: false,
        })
        .then(() => Api.bl.deleteType(type.id))
        .then(() => setTypes(old => removeItemAtIndex(old, old.findIndex(i => i.id === type.id))))
        .finally(hideModal);

    const confirmExtractCargo = (cargo: BLModel['innerCargo']) => showModal(ConfirmDialog,
        {
            description: cargo!.name,
        },
        {
            title: t('confirm_extract_cargo'),
            fullScreen: false,
        })
        .then(() => Api.bl.extractCargo(cargo!.id))
        .finally(hideModal);

    const confirmDeleteBL = (bl: BLModel) => showModal(ConfirmDialog,
        {
            description: bl.description,
        },
        {
            title: t('confirm_delete_bl'),
            fullScreen: false,
        })
        .then(() => Api.bl.deleteBL(bl.id))
        .finally(hideModal);

    const openGenerateNumbersModal = (id: number) => showModal(PromptDialog,
        {
            required: true,
            value: '1',
            description: t('bl_numbers_start_from')
        },
        {
            title: t('generate_bl_numbers'),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(start => Api.bl.generate(id, Number(start)))
        .then(hideModal);

    const openEditShipperModal = (bl: BLModel) => showModal(BLShipperDialog,
        {
            shipper: bl.shipper,
        },
        {
            title: t('edit_title', {value: t('shipper')})
        })
        .then(data => Api.bl.setShipper([bl.id], data))
        .then(hideModal);

    const openAssignShipperModal = (bls: BLModel[]) => showModal(BLShipperDialog,
        {
            shipper: {},
        },
        {
            title: t('set_shipper')
        })
        .then(data => Api.bl.setShipper(bls.map(bl => bl.id), data))
        .then(hideModal);

    const confirmDeleteShipper = (bl: BLModel) => showModal(ConfirmDialog,
        {
            description: bl.shipper.name
        },
        {
            title: t('confirm_delete_fake_shipper'),
            fullScreen: false,
        })
        .then(() => Api.bl.deleteShipper(bl.id))
        .finally(hideModal);

    return {
        openAddBLTypeModal,
        openEditBLTypeModal,
        confirmDeleteBLType,
        openGenerateNumbersModal,
        confirmExtractCargo,
        confirmDeleteBL,
        openEditShipperModal,
        openAssignShipperModal,
        confirmDeleteShipper,
    };
};

export default useBLModals;
